import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap, catchError, finalize } from 'rxjs/operators';
import { CaseTypeState } from '../../states';
import { ChecklistListState } from '../../states/checklist-list.state';
import { SaveChecklistResult } from './save-checklist.result';
import { ChecklistRepository, SaveChecklistParams } from '../../../infrastructure/repositories/checklist.repository';

@Injectable({ providedIn: 'root' })
export class SaveChecklistUseCase {
  private readonly repository = inject(ChecklistRepository);
  private readonly state = inject(CaseTypeState);
  private readonly checklistListState = inject(ChecklistListState);

  /**
   * Executes the use case to save a checklist for a case type.
   * @param caseTypeId - The case type ID
   * @param params - The checklist data to save
   * @returns Observable with the result containing the saved checklist ID
   */
  public execute(caseTypeId: string, params: SaveChecklistParams): Observable<SaveChecklistResult> {
    this.state.setSavingChecklist(true);
    this.state.clearErrorChecklist();

    return this.repository.saveChecklist(caseTypeId, params).pipe(
      tap(() => {
        this.checklistListState.clear();
      }),
      map((entity) => ({
        checklist: {
          id: entity.id,
          displayOnCases: entity.displayOnCases,
          steps: entity.steps,
        },
      })),
      catchError((error) => {
        const errorMessage = error?.error?.message || error?.message || 'Failed to save checklist';
        this.state.setErrorChecklist(errorMessage);
        throw error;
      }),
      finalize(() => {
        this.state.setSavingChecklist(false);
      })
    );
  }
}
